import React, { useEffect, useState } from 'react';
import { CareerData, PersonData } from '../../types';
import { careerParameterDefinitions } from '../../data/parameterDefinitions';
import { ParameterLabelWithTooltip } from '../common/Tooltip';

interface CareerInputProps {
  career: CareerData;
  person: PersonData;
  onChange: (careerId: string, field: string, value: any) => void;
  onDelete: (careerId: string) => void;
  simStartDate: string;
}

/**
 * Component for editing a single career
 */
export const CareerInput: React.FC<CareerInputProps> = ({
  career,
  person,
  onChange,
  onDelete,
  simStartDate
}) => {
  const [calculatedFields, setCalculatedFields] = useState({
    incomeInvestmentAmount: 0,
    incomeInvestmentStdDev: 0,
    employerMatchAmount: 0,
    employerMatchStdDev: 0,
    totalInvestmentAmount: 0,
    totalInvestmentStdDev: 0
  });

  // Local state for editing values (to allow display of raw input like "000")
  const [editingValues, setEditingValues] = useState<{[key: string]: string}>({});
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // Reset editing values when career changes from external source
  useEffect(() => {
    setEditingValues({});
  }, [career]);

  // Calculate current age for "Start Now" functionality
  const calculateCurrentAge = (birthdateString: string, simStartDateString: string): number => {
    if (!birthdateString || !simStartDateString) return 0;
    
    try {
      const birthDate = new Date(birthdateString);
      const simStartDate = new Date(simStartDateString);
      let age = simStartDate.getFullYear() - birthDate.getFullYear();
      const m = simStartDate.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && simStartDate.getDate() < birthDate.getDate())) {
        age--;
      }
      return Math.max(0, age);
    } catch (e) {
      console.error('Error calculating age:', e);
      return 0;
    }
  };

  // Update calculated fields when relevant values change
  useEffect(() => {
    const incomeInvestmentAmount = career.initialIncome.mean * (career.incomeInvestment.mean / 100);
    const employerMatchAmount = career.initialIncome.mean * (career.employerMatch.mean / 100);
    const otherInvestmentAmount = career.otherInvestment.mean;
    const totalInvestmentAmount = incomeInvestmentAmount + employerMatchAmount + otherInvestmentAmount;
    
    // Calculate standard deviation for income investment
    // Income Investment = initialIncome * (incomeInvestment / 100)
    // Using the formula for variance of a product of independent variables
    const incomeInvestmentVariance = Math.pow(career.initialIncome.stdDev * (career.incomeInvestment.mean / 100), 2) + 
                                   Math.pow(career.initialIncome.mean * (career.incomeInvestment.stdDev / 100), 2);
    const incomeInvestmentStdDev = Math.sqrt(incomeInvestmentVariance);
    
    // Calculate standard deviation for employer match
    // Emp Match = initialIncome * (employerMatch / 100)
    const employerMatchVariance = Math.pow(career.initialIncome.stdDev * (career.employerMatch.mean / 100), 2) + 
                            Math.pow(career.initialIncome.mean * (career.employerMatch.stdDev / 100), 2);
    const employerMatchStdDev = Math.sqrt(employerMatchVariance);
    
    // Calculate standard deviation for total investment
    // For independent random variables X and Y: Var(X + Y) = Var(X) + Var(Y)
    const otherInvestmentVariance = Math.pow(career.otherInvestment.stdDev, 2);
    const totalInvestmentVariance = incomeInvestmentVariance + employerMatchVariance + otherInvestmentVariance;
    const totalInvestmentStdDev = Math.sqrt(totalInvestmentVariance);
    
    setCalculatedFields({
      incomeInvestmentAmount,
      incomeInvestmentStdDev,
      employerMatchAmount,
      employerMatchStdDev,
      totalInvestmentAmount,
      totalInvestmentStdDev
    });
  }, [
    career.initialIncome.mean,
    career.initialIncome.stdDev,
    career.incomeInvestment.mean,
    career.incomeInvestment.stdDev,
    career.employerMatch.mean,
    career.employerMatch.stdDev,
    career.otherInvestment.mean,
    career.otherInvestment.stdDev
  ]);

  // Automatically sync start age with current age when "Now?" is checked
  useEffect(() => {
    if (career.isStartNow) {
      const birthdateParam = person.parameterValues.birthdate;
      const birthdateValue = birthdateParam && 'value' in birthdateParam ? birthdateParam.value as string : '';
      const currentAge = calculateCurrentAge(birthdateValue, simStartDate);
      
      // Only update if the calculated age differs from the current startAge
      if (currentAge !== career.startAge.mean) {
        onChange(career.id, 'startAge', { mean: currentAge, stdDev: 0 });
      }
    }
  }, [career.isStartNow, person.parameterValues.birthdate, simStartDate, career.startAge.mean, career.id, onChange]);

  // Automatically sync end age with retirement age when "Retire?" is checked
  useEffect(() => {
    if (career.isEndAtRetirement) {
      const retirementAgeParam = person.parameterValues.retirementAge;
      const retirementAge = retirementAgeParam && 'value' in retirementAgeParam ? retirementAgeParam.value as number : 65;
      
      // Only update if the retirement age differs from the current endAge
      if (retirementAge !== career.endAge.mean) {
        onChange(career.id, 'endAge', { mean: retirementAge, stdDev: 0 });
      }
    }
  }, [career.isEndAtRetirement, person.parameterValues.retirementAge, career.endAge.mean, career.id, onChange]);

  // Handle checkbox changes that affect age inputs
  const handleStartNowChange = (checked: boolean) => {
    onChange(career.id, 'isStartNow', checked);
    if (checked) {
      const birthdateParam = person.parameterValues.birthdate;
      const birthdateValue = birthdateParam && 'value' in birthdateParam ? birthdateParam.value as string : '';
      const currentAge = calculateCurrentAge(birthdateValue, simStartDate);
      onChange(career.id, 'startAge', { mean: currentAge, stdDev: 0 });
    }
  };

  const handleEndAtRetirementChange = (checked: boolean) => {
    onChange(career.id, 'isEndAtRetirement', checked);
    if (checked) {
      const retirementAgeParam = person.parameterValues.retirementAge;
      const retirementAge = retirementAgeParam && 'value' in retirementAgeParam ? retirementAgeParam.value as number : 65;
      onChange(career.id, 'endAge', { mean: retirementAge, stdDev: 0 });
    }
  };

  const getDisplayValue = (fieldKey: string, actualValue: number): string => {
    // If this field is being edited, show the raw editing value
    if (focusedField === fieldKey && editingValues[fieldKey] !== undefined) {
      return editingValues[fieldKey];
    }
    // Otherwise show the actual value
    return actualValue.toString();
  };

  const handleMeanStdDevChange = (field: string, type: 'mean' | 'stdDev', value: number) => {
    const currentValue = career[field as keyof CareerData] as { mean: number; stdDev: number };
    onChange(career.id, field, {
      ...currentValue,
      [type]: value
    });
  };

  const handleMeanStdDevInputChange = (field: string, type: 'mean' | 'stdDev', inputValue: string) => {
    const fieldKey = `${field}-${type}`;
    
    // Store the raw editing value
    setEditingValues(prev => ({ ...prev, [fieldKey]: inputValue }));
    
    // Update the actual value
    const normalizedValue = parseFloat(inputValue) || 0;
    handleMeanStdDevChange(field, type, normalizedValue);
  };

  const handleFocus = (field: string, type: 'mean' | 'stdDev', currentValue: string) => {
    const fieldKey = `${field}-${type}`;
    setFocusedField(fieldKey);
    setEditingValues(prev => ({ ...prev, [fieldKey]: currentValue }));
  };

  const handleBlur = (field: string, type: 'mean' | 'stdDev') => {
    const fieldKey = `${field}-${type}`;
    setFocusedField(null);
    // Clear the editing value to show the normalized version
    setEditingValues(prev => {
      const newState = { ...prev };
      delete newState[fieldKey];
      return newState;
    });
  };

  const formatCurrency = (value: number): string => {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  return (
    <div className="career-entry" data-career-id={career.id}>
      <div className="career-header">
        <div>Parameter</div>
        <div>Mean</div>
        <div>Std Dev</div>
        <div>Options</div>
      </div>

      <div className="career-params">
        {/* Start Age */}
        <div className="career-param-row">
          <ParameterLabelWithTooltip
            label="Start Age"
            description="How old this person will be when they begin this career. If the &quot;Now?&quot; box is checked, the career begins immediately."
          />
          <input
            type="number"
            value={getDisplayValue('startAge-mean', career.startAge.mean)}
            onChange={(e) => handleMeanStdDevInputChange('startAge', 'mean', e.target.value)}
            onFocus={(e) => handleFocus('startAge', 'mean', e.target.value)}
            onBlur={() => handleBlur('startAge', 'mean')}
            disabled={career.isStartNow}
            className="mean-input"
          />
          <input
            type="number"
            value={getDisplayValue('startAge-stdDev', career.startAge.stdDev)}
            onChange={(e) => handleMeanStdDevInputChange('startAge', 'stdDev', e.target.value)}
            onFocus={(e) => handleFocus('startAge', 'stdDev', e.target.value)}
            onBlur={() => handleBlur('startAge', 'stdDev')}
            disabled={career.isStartNow}
            min="0"
            className="stddev-input"
          />
          <div className="checkbox-container">
            <span className="unit">years</span>
            <input
              type="checkbox"
              id={`${person.id}-${career.id}-isStartNow`}
              checked={career.isStartNow}
              onChange={(e) => handleStartNowChange(e.target.checked)}
            />
            <label htmlFor={`${person.id}-${career.id}-isStartNow`}>Now?</label>
          </div>
        </div>

        {/* End Age */}
        <div className="career-param-row">
          <ParameterLabelWithTooltip
            label="End Age"
            description="At what age this person will finish this career. If the &quot;Retire?&quot; checkbox is checked, the age is set to the person&apos;s retirement age."
          />
          <input
            type="number"
            value={getDisplayValue('endAge-mean', career.endAge.mean)}
            onChange={(e) => handleMeanStdDevInputChange('endAge', 'mean', e.target.value)}
            onFocus={(e) => handleFocus('endAge', 'mean', e.target.value)}
            onBlur={() => handleBlur('endAge', 'mean')}
            disabled={career.isEndAtRetirement}
            className="mean-input"
          />
          <input
            type="number"
            value={getDisplayValue('endAge-stdDev', career.endAge.stdDev)}
            onChange={(e) => handleMeanStdDevInputChange('endAge', 'stdDev', e.target.value)}
            onFocus={(e) => handleFocus('endAge', 'stdDev', e.target.value)}
            onBlur={() => handleBlur('endAge', 'stdDev')}
            disabled={career.isEndAtRetirement}
            min="0"
            className="stddev-input"
          />
          <div className="checkbox-container">
            <span className="unit">years</span>
            <input
              type="checkbox"
              id={`${person.id}-${career.id}-isEndAtRetirement`}
              checked={career.isEndAtRetirement}
              onChange={(e) => handleEndAtRetirementChange(e.target.checked)}
            />
            <label htmlFor={`${person.id}-${career.id}-isEndAtRetirement`}>Retire?</label>
          </div>
        </div>

        {/* Standard Parameters */}
        {careerParameterDefinitions.map((paramDef) => (
          <React.Fragment key={paramDef.id}>
            <div className="career-param-row">
              <ParameterLabelWithTooltip
                label={paramDef.label}
                description={paramDef.description}
              />
              <input
                type="number"
                value={getDisplayValue(`${paramDef.id}-mean`, (career[paramDef.id as keyof CareerData] as { mean: number }).mean)}
                onChange={(e) => handleMeanStdDevInputChange(paramDef.id, 'mean', e.target.value)}
                onFocus={(e) => handleFocus(paramDef.id, 'mean', e.target.value)}
                onBlur={() => handleBlur(paramDef.id, 'mean')}
                step={paramDef.isPercent ? '0.1' : 'any'}
                className="mean-input"
              />
              <input
                type="number"
                value={getDisplayValue(`${paramDef.id}-stdDev`, (career[paramDef.id as keyof CareerData] as { stdDev: number }).stdDev)}
                onChange={(e) => handleMeanStdDevInputChange(paramDef.id, 'stdDev', e.target.value)}
                onFocus={(e) => handleFocus(paramDef.id, 'stdDev', e.target.value)}
                onBlur={() => handleBlur(paramDef.id, 'stdDev')}
                step={paramDef.isPercent ? '0.1' : '1'}
                min="0"
                className="stddev-input"
              />
              <span className="unit">{paramDef.unit}</span>
            </div>
            
            {/* Add inflation adjustment checkbox after Income Increase */}
            {paramDef.id === 'annualIncomeIncrease' && (
              <div className="career-param-row">
                <ParameterLabelWithTooltip
                  label="Inflation Adjusted"
                  description="If checked, each year's income increase will include that year's inflation rate in addition to the Income Increase percentage. This means you expect your employer to adjust your income for inflation (upwards only) and also give you the estimated raise."
                />
                <div className="checkbox-container" style={{ gridColumn: '2 / 5', justifyContent: 'flex-start', paddingLeft: '10px' }}>
                  <input
                    type="checkbox"
                    id={`${person.id}-${career.id}-inflationAdjustedIncrease`}
                    checked={career.inflationAdjustedIncrease}
                    onChange={(e) => onChange(career.id, 'inflationAdjustedIncrease', e.target.checked)}
                  />
                  <label htmlFor={`${person.id}-${career.id}-inflationAdjustedIncrease`}>Raise includes inflation</label>
                </div>
              </div>
            )}
          </React.Fragment>
        ))}

        {/* Calculated Fields */}
        <div className="career-param-row calculated-field">
          <ParameterLabelWithTooltip
            label="Income Investment ($)"
            description="The dollar amount of income to be invested. This is calculated as <code>Initial Income * Income Investment %</code>."
          />
          <span className="calculated-value">
            {formatCurrency(calculatedFields.incomeInvestmentAmount)}
          </span>
          <span className="calculated-value">
            ±{formatCurrency(calculatedFields.incomeInvestmentStdDev)}
          </span>
          <span className="unit">$ / year</span>
        </div>

        <div className="career-param-row calculated-field">
          <ParameterLabelWithTooltip
            label="Emp Match ($)"
            description="The dollar amount that the employer is expected to contribute to the retirement account."
          />
          <span className="calculated-value">
            {formatCurrency(calculatedFields.employerMatchAmount)}
          </span>
          <span className="calculated-value">
            ±{formatCurrency(calculatedFields.employerMatchStdDev)}
          </span>
          <span className="unit">$ / year</span>
        </div>

        <div className="career-param-row calculated-field">
          <ParameterLabelWithTooltip
            label="Total Investment ($)"
            description="The total dollar amount of investments to be made based on the above sources."
          />
          <span className="calculated-value">
            {formatCurrency(calculatedFields.totalInvestmentAmount)}
          </span>
          <span className="calculated-value">
            ±{formatCurrency(calculatedFields.totalInvestmentStdDev)}
          </span>
          <span className="unit">$ / year</span>
        </div>
      </div>

      {/* Delete Button */}
      <div className="career-actions">
        <button
          type="button"
          onClick={() => onDelete(career.id)}
          className="delete-career-btn"
        >
          Delete Career
        </button>
      </div>
    </div>
  );
};

interface CareerListProps {
  careers: CareerData[];
  person: PersonData;
  onCareerChange: (careerId: string, field: string, value: any) => void;
  onCareerDelete: (careerId: string) => void;
  onCareerAdd: () => void;
  simStartDate: string;
}

/**
 * Component for managing a list of careers for a person
 */
export const CareerList: React.FC<CareerListProps> = ({
  careers,
  person,
  onCareerChange,
  onCareerDelete,
  onCareerAdd,
  simStartDate
}) => {
  const maxCareers = 5;

  return (
    <div className="career-list">
      {careers.length === 0 && (
        <div className="no-careers-message">
          <p>No careers defined. This person will make no further investments.</p>
        </div>
      )}
      
      {careers.map((career) => (
        <CareerInput
          key={career.id}
          career={career}
          person={person}
          onChange={onCareerChange}
          onDelete={onCareerDelete}
          simStartDate={simStartDate}
        />
      ))}
      
      {careers.length < maxCareers && (
        <button
          type="button"
          onClick={onCareerAdd}
          className="add-career-btn"
        >
          + Add Career
        </button>
      )}
    </div>
  );
};
